"use client";

import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ReadyToSubmitCustomers() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const reassurances = ["No upfront costs", "Performance-based model", "Ready-to-submit leads"];
  return <section data-section-id="4198" id="ready-to-submit-customers" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32 md:py-36 px-6 sm:px-8 border-t border-border/20">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-70">
        <svg viewBox="0 0 1600 200" preserveAspectRatio="xMidYMid meet" className={`w-[140%] max-w-none transition-all duration-[2000ms] ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`} style={{
        color: "var(--accent)"
      }}>
          <path d="M0 100 L300 100 L340 100 L360 60 L390 140 L420 40 L450 160 L480 100 L700 100 L740 100 L760 70 L790 130 L820 50 L850 150 L880 100 L1100 100 L1140 100 L1160 65 L1190 135 L1220 45 L1250 155 L1280 100 L1600 100" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.12" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div ref={sectionRef} className="relative mx-auto max-w-4xl flex flex-col items-center text-center">
        <div className={`flex items-center gap-3 mb-8 sm:mb-10 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="h-px w-6 bg-accent" aria-hidden="true" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-accent font-semibold">
            § Scale
          </span>
          <span className="h-px w-6 bg-accent" aria-hidden="true" />
        </div>

        <h2 className={`font-default font-bold tracking-tight text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.08] mb-10 md:mb-12 transition-all duration-1000 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Scale your agency with{" "}
          <span className="italic font-serif text-accent font-normal">qualified insurance leads</span>
          .
        </h2>

        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-12 md:mb-14 w-full sm:w-auto transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild className="w-full sm:w-auto h-14 px-8 text-base font-semibold tracking-normal bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md hover:shadow-lg transition-all duration-200 group">
            <Link to="/GetStartedPage">
              <span>Get Started Today</span>
              <ArrowRight className="size-4.5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Button>

          <Link to="tel:+13309341671" className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 h-14 rounded-md border border-border/80 bg-card/60 hover:bg-card hover:border-accent/50 text-foreground hover:text-accent transition-all duration-200 font-medium text-base shadow-xs hover:shadow-sm group">
            <Phone className="size-4 text-muted-foreground group-hover:text-accent transition-colors duration-200" />
            <span>
              or call{" "}
              <span className="underline underline-offset-4 decoration-border group-hover:decoration-accent font-semibold">
                +1 (330) 934-1671
              </span>
            </span>
          </Link>
        </div>

        <div className={`w-full max-w-sm transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-50"}`}>
          <Separator className="bg-border/40" />
        </div>

        <ul className={`mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 font-sans text-xs sm:text-sm text-muted-foreground transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {reassurances.map((item, i) => <li key={i} data-index={i} className="flex items-center gap-2.5">
              <span className="size-1.5 rounded-sm bg-accent inline-block shrink-0" aria-hidden="true" />
              <span className="tracking-wide font-medium">{item}</span>
              {i < reassurances.length - 1 && <span className="hidden sm:inline text-border/60 ml-6" aria-hidden="true">
                  /
                </span>}
            </li>)}
        </ul>
      </div>
    </section>;
}